<template>
    <v-app>
        <div ref="application">

            <toolbar v-if="userAuthenticated" :update-available="updateAvailable" :application-build="applicationBuild"></toolbar>

            <flash-message></flash-message>

            <progress-indicator></progress-indicator>

            <v-dialog
                persistent
                v-model="loadingDialog"
                max-width="300"
            >
                <v-card>
                    <v-card-text>
                        <v-row>
                            <v-col cols="12">
                                <v-progress-linear
                                    :size="70"
                                    :width="7"
                                    color="green lighten-1"
                                    indeterminate
                                    query
                                ></v-progress-linear>
                            </v-col>
                            <v-col cols="12" class="text-center">
                                <span class="display-1">LOADING...</span>
                            </v-col>
                        </v-row>
                    </v-card-text>
                </v-card>

            </v-dialog>

            <navigation-drawer v-if="userAuthenticated"></navigation-drawer>

            <announcements v-if="userAuthenticated"></announcements>

            <chat v-if="userAuthenticated"></chat>

            <!-- Router view rendered here -->
            <v-main>
                <keep-alive>
                    <router-view></router-view>
                </keep-alive>
            </v-main>
        </div>
    </v-app>

</template>

<script>

import Echo from 'laravel-echo/dist/echo';
import NavigationDrawer from './NavigationDrawer.vue';
import Toolbar from './Toolbar.vue';
import Announcements from '../../modules/Messages/components/index.vue';
import Chat from '../../modules/Chat/components/TeamChat.vue';
import ProgressIndicator from './ProgressIndicator';
import FlashMessage from './FlashMessage';
import {timeDisplay} from "../mixins/timeDisplayMixin";
import * as am4core from "@amcharts/amcharts4/core";
import {mapActions, mapMutations} from 'vuex';

export default {

    mixins: [timeDisplay],

    components: {
        NavigationDrawer,
        Announcements,
        Toolbar,
        Chat,
        ProgressIndicator,
        FlashMessage,
    },

    data() {
        return {
            echo: null,
            desktop: true,
            loadingDialog: false,
            userIdle: false,
            showProgress: false,
            applicationBuild: 1,
            updateAvailable: false,
            updateSoundPlayed: false,
        }
    },

    computed: {
        userAuthenticated() {
            return this.$store.state.session.auth;
        },
        trialMessage() {
            if (this.$store.state.PanelSettings.settings.trial_expires_on) {
                return 'Your trial ends ' + this.toHumanTill(this.$store.state.PanelSettings.settings.trial_expires_on);
            }
        },
    },

    created() {

        const loader = document.getElementById('ninja-loader');

        if (loader) {
            loader.remove();
        }

        this.registerEventListeners();
        this.saveSparkData();
        this.getPanelSettings();
        this.checkForUpdates();
    },

    onIdle() {

        if (this.userAuthenticated) {

            this.userIdle = true;

            axios.put('/user/idle');

            if (this.updateAvailable) {
                window.location.reload();
            }

            Bus.$emit('flash-message', {duration: 5000, message: "You went idle..."});
        }
    },

    onActive() {
        if (this.userAuthenticated && this.userIdle) {
            this.userIdle = false;

            axios.put('/user/active');

            Bus.$emit('flash-message', {duration: 5000, message: 'Welcome back... hope you had a good break!'});
        }
    },

    methods: {

        ...mapActions([
            'loadTimezones',
        ]),

        ...mapMutations([
            'setEcho',
        ]),

        checkForUpdates() {
            setInterval(() => {

                axios.get('/build')
                    .then(({data}) => {

                        if (data.build !== this.$store.state.settings.build) {

                            this.applicationBuild = data.build;
                            this.updateAvailable = true;

                            if (!this.updateSoundPlayed && !this.userIdle) {
                                let audio = new Audio('/sounds/announcement.mp3');
                                audio.volume = 0.2;
                                audio.play();
                                this.updateSoundPlayed = true;
                            }

                            if (this.userIdle) window.location.reload();
                        }
                    });

            }, 1000 * 60 * 5);
        },

        createEchoInstance(pusher) {

            this.echo = new Echo({
                broadcaster: 'pusher',
                key: pusher.key,
                cluster: pusher.cluster,
                encrypted: true,
            });

            // Store echo to Vuex
            this.setEcho(this.echo);
        },

        getPanelSettings() {

            if (this.userAuthenticated) {
                this.$store.dispatch('PanelSettings/getSettings')
                    .then(() => {
                        this.showTrialMessage();
                    })
                    .catch(() => {
                        Bus.$emit('server-error');
                    });
            }
        },

        saveSparkData() {

            this.$store.commit('setSettings', Spark.settings);

            am4core.addLicense(Spark.amcharts.license);

            if (Spark.session.auth) {
                this.$store.commit('setUser', Spark.user);
                this.$store.commit('setSession', Spark.session);

                this.createEchoInstance(Spark.pusher);
            }
        },

        showTrialMessage() {
            if (this.$store.state.PanelSettings.settings.trial_expires_on !== null) {

                Bus.$emit('flash-message', {
                    type: 'info',
                    duration: 10000,
                    message: this.trialMessage, location: 'bottom'
                });
            }
        },

        sessionExpired() {

            Bus.$emit('navigation-drawer', false);
            Bus.$emit('chat-drawer', false);
            Bus.$emit('toolbar', false);

            this.$router.push('/spa/sessionexpired');
        },

        registerEventListeners() {

            // Event handler for user authenticated
            Bus.$on('user-authenticated', () => {
                this.loadingDialog = true;

                axios.put('/user/active');

                // Get updated user info
                axios.get('/sparkdata')
                    .catch(error => {
                        this.sessionExpired();
                    })
                    .then(({data}) => {
                        // Merge new properties with Spark
                        Object.assign(Spark, data.spark);

                        this.saveSparkData();

                        this.loadingDialog = false;

                        Bus.$emit('flash-message', {'message': 'Welcome ' + this.$store.state.user.name + '!'});

                        // Fetch panel settings
                        this.getPanelSettings();

                        // Change view to dashboard
                        this.$router.push('/spa/dashboard');
                    });
            });

            Bus.$on('show-session-expired', () => {
                this.sessionExpired();
            });

            Bus.$on('show-trial-expired', () => {
                this.sendToUrl('/logout')
            });
        },
    }
}

</script>
